import {untilDestroyed, UntilDestroy} from '@ngneat/until-destroy';
import {Component, Inject, OnInit} from '@angular/core';
import {ViewConfigPayload, WORKBENCH_VIEW_CONFIG, WorkbenchViewConfig} from '../../../shared/types';
import {DeviceId, ProjectId, UserId} from '@webmate-web-core/shared/types/ids';
import {DevicePropertyNames, getPlatformFamily, PlatformFamily
} from '@webmate-web-core/shared/types/devices/device-properties';
import {DeviceService} from '@webmate-web-core/shared/services/device/device.service';
import {FavoriteDevice, FavoriteDeviceService} from '@webmate-web-core/shared/services/device/favorite-device.service';
import {Device} from '@webmate-web-core/shared/types/devices/device';
import {faDesktop, faUpload} from '@fortawesome/pro-light-svg-icons';
import {faInfoCircle, faTrash, faBan} from '@fortawesome/pro-solid-svg-icons';
import {WorkbenchViewComponent} from '../../../shared/components/viewcontainers/workbench-view.component';
import {WorkbenchLayoutManager} from '../../../shared/services/workbench-layout-manager.service';
import {WorkbenchContextService} from '../../../shared/services/workbench-context.service';
import {ConsolePlugin} from '../../console/console-plugin';
import {distinctUntilChanged, map, switchMap, take} from 'rxjs/operators';
import {ModalToolContainerService} from '@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.service';
import {WmNotificationService} from '@webmate-web-core/shared/components/notification/wm-notification.service';
import {DeviceSlotService} from '@webmate-web-core/shared/services/device/device-slot.service';
import {ConsoleAccessService} from '@webmate-web-core/shared/services/device/console-access.service';
import {UserService} from '@webmate-web-core/shared/services/user.service';
import {UserInfo} from '@webmate-web-core/shared/types/user/user-info';
import {Ticket} from '@webmate-web-core/shared/types/ticket';
import {
    DeployDeviceDialogComponent,
    DeployDeviceDialogToolConfig
} from '@webmate-web-core/shared/tools/device-deployment/deploy-device-dialog.component';
import {Platform} from '@webmate-web-core/shared/types/platform';
import {LogoResolverService} from '@webmate-web-core/shared/services/logo-resolver.service';
import {combineLatest, Observable, of} from 'rxjs';
import {
    constructRequirementsForDeployableInSlot,
    Deployable,
    DeviceOfferId,
    isAvailable
} from '@webmate-web-core/shared/types/devices/device-template';
import {DeployableService} from '@webmate-web-core/shared/services/device/deployable.service';
import {Resolution} from '@webmate-web-core/shared/types/resolution';

export interface DeployedDevicesView extends ViewConfigPayload {
    data: {
        platformFamily: PlatformFamily
    },
}

/*
 * TODO: put deployable logic into common helper (and use that also in project-device-deployment.component.ts)
 */

interface FavoriteDeviceDisplay extends FavoriteDevice {
    name?: string;
    logo?: Observable<string>;
}

@UntilDestroy()
@Component({
    templateUrl: './deployed-devices-view.component.html',
    styleUrls: ['./deployed-devices-view.component.less']
})
export class DeployedDevicesViewComponent extends WorkbenchViewComponent implements OnInit {

    projectId: ProjectId;
    userId: UserId;

    groupBy: string = 'SYSTEM'; //TODO enum?

    deployedDevices?: Device[] = undefined;
    isAccessedByDifferentUser: { [id: DeviceId]: boolean } = {};
    otherUsersAccessingDevice: { [id: DeviceId]: UserInfo[] } = {};
    isAutomaticallyInUse: { [id: DeviceId]: boolean } = {};
    isReservedByAnotherUser: { [id: DeviceId]: boolean } = {};
    hasAnnotations: { [id: DeviceId]: boolean } = {};
    annotationStatus: { [id: DeviceId]: string } = {};
    mayAccessConsole = false;
    exclusiveConsole = true;
    reservationsFeatureActive = false;
    activeDevice: DeviceId | undefined;
    favorites: FavoriteDeviceDisplay[];
    activeFavoriteId: string;
    availableDeployables: Deployable[];
    allowAutomation: boolean = true;
    desiredResolution: Resolution = new Resolution(1920, 1080);
    selectedDeployable: Deployable | undefined;

    constructor(@Inject(WORKBENCH_VIEW_CONFIG) public config: WorkbenchViewConfig<DeployedDevicesView>,
                private deviceService: DeviceService,
                private favoriteDeviceService: FavoriteDeviceService,
                private workbenchContextService: WorkbenchContextService,
                private toolService: ModalToolContainerService,
                layoutManager: WorkbenchLayoutManager,
                private logoService: LogoResolverService,
                private deployableService: DeployableService,
                private deviceSlotService: DeviceSlotService,
                private notificationService: WmNotificationService,
                private consoleAccessService: ConsoleAccessService,
                private userService: UserService) {
        super(layoutManager, config);
    }

    ngOnInit() {
        this.projectId = this.workbenchContextService.getProjectId();
        this.userId = this.workbenchContextService.getUserId();

        this.consoleAccessService.getOpenConsolePermission$(this.projectId).pipe(untilDestroyed(this)).subscribe(permission => {
            this.mayAccessConsole = permission;
        });
        this.consoleAccessService.getExclusiveConsoleFeatureActive$().pipe(untilDestroyed(this)).subscribe(active => {
            this.exclusiveConsole = active;
        });
        this.consoleAccessService.getReservationsFeatureActive$().pipe(untilDestroyed(this)).subscribe(active => {
            this.reservationsFeatureActive = active;
            this.refreshSlotDependentChecks();
        });

        this.deviceService.getDevicesForProject$(this.projectId).pipe(untilDestroyed(this)).subscribe(devices => {
            this.deployedDevices = devices.filter(device => !device.isHeadlessDevice()).filter(device => {
                const platformFamily = this.config.payload.data.platformFamily;
                return getPlatformFamily(device.properties) === platformFamily;
            });
            this.isAccessedByDifferentUser = {};
            this.isAutomaticallyInUse = {};
            this.deployedDevices.forEach(device => {
                const otherUsersTickets = this.consoleAccessService.findOtherUsersConsoleAccessTickets(device, this.userId);
                this.isAccessedByDifferentUser[device.id] = otherUsersTickets.length > 0;
                this.isAutomaticallyInUse[device.id] = device.properties[DevicePropertyNames.ActiveVehicles]?.length > 0;
                this.refreshOtherUsersAccessingDevice(device.id, otherUsersTickets);
            });
            this.refreshSlotDependentChecks();
        });
        this.workbenchContextService.subscribeWorkbenchContext$().pipe(untilDestroyed(this)).subscribe(context => {
            this.activeDevice = context.deviceContext?.activeDevice;
            const device = this.deployedDevices?.find(d => d.id === this.activeDevice);
            const keyMap: Record<string, string> = { 'MACOS': 'macvirt.template.id', 'WINDOWS': 'vcloud.templateId' };
            const propertyKey = this.config.payload.data.platformFamily === 'mobile' ? 'machine.id' : keyMap[device?.platform?.platformType!];
            this.activeFavoriteId = device?.properties?.[propertyKey];
        });

        /* the deployables are needed for the favorites strip only (availability overlay and deployment), so we do not
         * ask every slot of the project for its offers while there is no favorite to resolve */
        this.favoriteDeviceService.getFavoriteDevices$(this.config.payload.data.platformFamily, this.projectId).pipe(
            map(favorites => favorites.length > 0),
            distinctUntilChanged(),
            switchMap(hasFavorites => hasFavorites
                ? this.deployableService.getDeployablesForProject$(this.projectId, this.config.payload.data.platformFamily, true)
                : of([] as Deployable[])),
            untilDestroyed(this)
        ).subscribe(deployables => {
            this.availableDeployables = this.filterAvailableDeployables(deployables);
        });

        this.getFavorites();
    }

    accessConsole(device: Device) {
        if (!this.isConsoleAccessible(device)) {
            return;
        }
        const request = () => {
            this.layoutManager.instantiateAndShowViewTemplate(ConsolePlugin.ViewTemplateId, {id: device.id, name: device.name});
        };
        if (this.isAutomaticallyInUse[device.id]) {
            this.notificationService.open('This device is currently used by an automated test. Do you really want to access it? ', 'Yes', request, 'No');
        } else if (this.isAccessedByDifferentUser[device.id]) {
            this.notificationService.open('This device is currently accessed by another user. Do you really want to access it?', 'Yes', request, 'No');
        } else {
            request();
        }
    }

    deleteDevice(deviceId: DeviceId) {
        // Close the console before firing the release: the server tears our console session down as part
        // of the release, and the resulting DISCONNECTED (VNC socket drop / DeviceConsole.Terminated) would
        // otherwise flash the "Reconnect" button until the response arrives.
        const consoleViewId = 'device.view.console.' + deviceId;
        if (this.layoutManager.isViewRegistered(consoleViewId)) {
            this.layoutManager.closeView(consoleViewId);
        }
        this.deviceService.requestReleaseOfDevice$(deviceId).pipe(take(1), untilDestroyed(this)).subscribe({
            next: () => {},
            error: () => this.notificationService.toastWarningNotification('Request for device release failed!')
        });
    }

    private lastCheckedOtherUserIdsKey: { [id: DeviceId]: string } = {};

    /** Resolves the names of the users behind the given tickets, for the "accessed by ..." tooltip - keyed
     * by device so unrelated devices in the list don't refetch on every emission. */
    private refreshOtherUsersAccessingDevice(deviceId: DeviceId, otherUsersTickets: Ticket[]): void {
        const uniqueUserIds = Array.from(new Set(otherUsersTickets.map(t => t.userId)));
        const key = uniqueUserIds.slice().sort((a, b) => a.localeCompare(b)).join(',');
        if (this.lastCheckedOtherUserIdsKey[deviceId] === key) {
            return;
        }
        this.lastCheckedOtherUserIdsKey[deviceId] = key;
        if (uniqueUserIds.length === 0) {
            this.otherUsersAccessingDevice[deviceId] = [];
            return;
        }
        combineLatest(uniqueUserIds.map(id => this.userService.getInformationAboutUser(id))).pipe(take(1), untilDestroyed(this)).subscribe(infos => {
            this.otherUsersAccessingDevice[deviceId] = infos;
        });
    }

    /** Mirrors DeviceSummaryComponent.getAccessUsers(), adapted to look a specific device up in the map. */
    getAccessUsers(device: Device): string {
        const users = this.otherUsersAccessingDevice[device.id] ?? [];
        const base = 'Device currently accessed by ';
        if (users.length <= 1) {
            return base + (users[0]?.name ?? '');
        }
        const msg = base + users.map(u => u.name).join(', ');
        const pos = msg.lastIndexOf(',');
        return msg.substring(0, pos) + ' and' + msg.substring(pos + 1);
    }

    /** Looks up each deployed device's slot (bulk-fetched, cached per project) to evaluate reservation and
     * annotation-update state, mirroring the checks DeviceSummaryComponent applies for a single device. */
    private refreshSlotDependentChecks(): void {
        if (!this.deployedDevices) {
            return;
        }
        this.deviceSlotService.getAvailableSlotsForProject$(this.projectId).pipe(take(1), untilDestroyed(this)).subscribe(slots => {
            this.deployedDevices!.forEach(device => {
                const slot = slots.find(s => s.deviceId === device.id);
                if (!slot) {
                    return;
                }
                if (this.reservationsFeatureActive) {
                    const activeReservation = slot.reservations?.find(r => r.isActive);
                    this.isReservedByAnotherUser[device.id] = activeReservation !== undefined && activeReservation.userId !== this.userId;
                }
                this.deployableService.getDeployablesForSlot$(slot.id).pipe(take(1), untilDestroyed(this)).subscribe(deployables => {
                    const deployable = deployables.find(d => d.properties[DevicePropertyNames.MachineId] === device.properties[DevicePropertyNames.MachineId]);
                    const annotations = deployable?.properties[DevicePropertyNames.Annotations];
                    this.hasAnnotations[device.id] = !!annotations;
                    this.annotationStatus[device.id] = annotations?.['status'];
                });
            });
        });
    }

    isConsoleAccessible(device: Device) {
        return this.consoleAccessService.isConsoleAccessible({
            mayAccessConsole: this.mayAccessConsole && !!device.properties[DevicePropertyNames.Console],
            belongsToDifferentProject: false,
            isAccessedByDifferentUser: !!this.isAccessedByDifferentUser?.[device.id],
            exclusiveConsole: this.exclusiveConsole,
            isReservedByAnotherUser: !!this.isReservedByAnotherUser?.[device.id],
            hasAnnotations: !!this.hasAnnotations?.[device.id],
            annotationStatus: this.annotationStatus?.[device.id],
        });
    }

    generateConsoleAccessTooltip(device: Device) {
        if (this.isConsoleAccessible(device)){
            return undefined;
        }
        if (!this.isConsoleAccessible(device)){
            return 'Console is currently unavailable';
        }
    }

    openDeviceDeploymentTool() {
        this.toolService.openTool(DeployDeviceDialogComponent, new DeployDeviceDialogToolConfig({
            projectId: this.projectId,
            platformFamily: this.config.payload.data.platformFamily
        }))
    }

    getFavorites(): void {
        const family = this.config.payload.data.platformFamily;
        this.favoriteDeviceService.getFavoriteDevices$(family, this.projectId)
            .pipe(untilDestroyed(this))
            .subscribe(rawFavorites => {
                this.favorites = rawFavorites.map(item => {
                    const isDesktop = family === 'desktop' && item.platform;
                    const name = isDesktop ? `${item.platform!.platformType} ${item.platform!.platformVersion}` : `${item.manufacturerName} ${item.modelName}`;
                    const logo = isDesktop ? this.logoService.getLogoForPlatform(new Platform(item.platform!.platformType, item.platform!.platformVersion, item.platform!.platformArchitecture)) : this.logoService.getLogoForModel(item.manufacturerName!, item.modelName!);
                    return { ...item, name, logo };
                });
            });
    }

    deployDevice(deployableId: DeviceOfferId) {
        this.selectedDeployable = this.availableDeployables
            ?.find(g => g.id === deployableId)

        if (!this.selectedDeployable) {
            return;
        }

        let requirements = constructRequirementsForDeployableInSlot(this.selectedDeployable, this.selectedDeployable.properties[DevicePropertyNames.SlotId], this.allowAutomation, this.selectedSupportsResolution ? this.desiredResolution : undefined);
        const name = (this.config.payload.data.platformFamily == 'desktop' ) ? this.selectedDeployable.platform.platformType + ' ' + this.selectedDeployable.platform.platformVersion : this.selectedDeployable.properties?.[DevicePropertyNames.ModelName];
        this.deviceService.requestDeployment$(this.projectId, requirements, name).subscribe(
            next => {
                this.layoutManager.instantiateAndShowViewTemplate(ConsolePlugin.ViewTemplateId, {
                    id: next.id,
                    name: next.name
                });
            },
            err => {
                const msg = "Request for deployment failed, error was: " + ((typeof(err['error']) === 'string' ? err['error'] : undefined) || 'unknown');
                console.error(msg, err);
            }
        );
    }

    private filterAvailableDeployables(deployables: Deployable[]): Deployable[] {
        // TODO custom sorting function?
        return deployables.filter(isAvailable).sort((a,b) => a.runtimeCost - b.runtimeCost);
    }

    get selectedSupportsResolution() {
        if (!this.selectedDeployable) {
            return;
        }
        return !!this.selectedDeployable.properties[DevicePropertyNames.ChangeResolution];
    }

    isAvailable(favId: string): boolean {
        const availableForDeployment = this.availableDeployables?.some(d => d.id === favId);
        return !!(availableForDeployment );
    }

    protected readonly faDesktop = faDesktop;
    protected readonly faTrash = faTrash;
    protected readonly faInfoCircle = faInfoCircle;
    protected readonly faUpload = faUpload;
    protected readonly faBan = faBan;
}
